import dayjs from "dayjs"
import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
import { IMeter } from "@/models/meter/meter.types"
import { IRegister } from "@/models/meterRegister/meterRegister.types"
import { Meterpoint } from "@/models/meterpoint/meterpoint"
import { Meter } from "@/models/meter/meter"
import { Register } from "@/models/meterRegister/meterRegister"
import { Reading } from "@/models/meterReading/meterReading"
import { ReadingSource } from "@/models/meterReading/meterReading.types"
import { ISubmitReadings } from "./MeterReadingsMutator.types"

const createCustomerReading = (
  reading: number,
  last: { id: number; unit: string; cumulative: number },
  submission: ISubmitReadings,
): Reading =>
  new Reading(
    last.id + 1,
    "Customer" as ReadingSource,
    reading,
    dayjs(submission.date),
    last.unit,
    submission.sequenceType,
    "Normal",
    reading - last.cumulative,
    "Pending",
  )

const updateRegisterWithReading = (
  register: IRegister,
  submission: ISubmitReadings,
): IRegister => {
  const submittedEntry = submission.readings.find(
    ([, reg]) => reg.identifier === register.identifier,
  )

  if (!submittedEntry) {
    return register
  }

  const [reading] = submittedEntry
  const lastReading = register.readings.at(-1) ?? {
    id: 0,
    unit: "",
    cumulative: 0,
  }

  return new Register(
    register.id,
    register.identifier,
    register.rateName,
    register.usage,
    [
      ...register.readings,
      createCustomerReading(reading, lastReading, submission),
    ],
  )
}

const updateMeter = (meter: IMeter, submission: ISubmitReadings): IMeter => {
  if (meter.identifier !== submission.meter.identifier) {
    return meter
  }

  return new Meter(
    meter.id,
    meter.identifier,
    meter.meterType,
    meter.meterTypeDescription,
    meter.registers.map((register) =>
      updateRegisterWithReading(register, submission),
    ),
  )
}

export const insertReadingsIntoMeterpointCache = (
  submission: ISubmitReadings,
  meterpointsCache: IMeterpoint[],
): IMeterpoint[] =>
  meterpointsCache.map((meterpoint) =>
    meterpoint.identifier === submission.meterpoint.identifier
      ? new Meterpoint(
          meterpoint.id,
          meterpoint.identifier,
          meterpoint.meterpointType as "MPAN" | "MPRN",
          meterpoint.supplyStatus,
          meterpoint.supplyStartDate,
          meterpoint.operationType,
          meterpoint.serviceType,
          meterpoint.meters.map((meter) => updateMeter(meter, submission)),
          meterpoint.gsp,
          meterpoint.profileClass,
          meterpoint.dno,
          meterpoint.dcc,
          meterpoint.mam,
          meterpoint.mop,
        )
      : meterpoint,
  )
